import { DataTable } from "../../Components/ui/table";
import { DataTableColumn } from "../../Components/ui/table/types";
import { PageLayout } from "../../Components/PageLayout";

import { usePaginatedQuery } from "../../Hooks/use-paginated-query";
import { CursorPagination } from "../../Components/ui/table/pagination";
import { FiltersWrapper } from "../../Components/ui/table/filters";
import { InputFilter } from "../../Components/ui/table/filters/input";

import { useMemo, useState } from "react";
import { StatusBadge } from "../../Components/ui/statusBadge";
import { useNavigate } from "react-router-dom";

import { Button } from "../../Components/ui/button";
import { CreateTrailerAssetForm } from "./create-trailer-asset";
import { SelectFilter } from "../../Components/ui/table/filters/select/select-filter";

type TrailerAssetRow = {
  id: number;
  unit: string;
  license_plate: string;
  location?: { loc_name?: string | null } | null;
  is_active: boolean;
};

const columns: DataTableColumn<TrailerAssetRow>[] = [
  {
    header: "Unit",
    labelKey: "unit",
    field: "unit",
    sortable: true,
  },
  {
    header: "License Plate",
    labelKey: "licensePlate",
    field: "license_plate",
    sortable: true,
  },
  {
    header: "Location",
    labelKey: "location",
    accessor: (row: TrailerAssetRow) => row.location?.loc_name ?? "-",
  },
  {
    header: "Status",
    labelKey: "status",
    cell: (row: TrailerAssetRow) => {
      const isActive = row.is_active;
      return (
        <StatusBadge
          variant={isActive ? "positive" : "negative"}
          labelKey={isActive ? "active" : "inactive"}
        />
      );
    },
  },
];

const initialUrl = "/assets/trailer-assets/";

const TrailerAssetsRoute = () => {
  const [unitFilter, setUnitFilter] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [statusFilter, setStatusFilter] = useState("true");

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const navigate = useNavigate();

  const params = useMemo(
    () => ({
      is_active: statusFilter === "true",
      ...(unitFilter.trim() ? { unit: unitFilter.trim() } : {}),
      ...(licensePlate.trim() ? { license_plate: licensePlate.trim() } : {}),
    }),
    [unitFilter, licensePlate, statusFilter],
  );

  const results = usePaginatedQuery<TrailerAssetRow>({
    initialUrl: initialUrl,
    params: params,
  });

  return (
    <PageLayout title="Trailer Assets" header="trailerAssets">
      {createModalOpen && (
        <CreateTrailerAssetForm cancel={() => setCreateModalOpen(false)} />
      )}
      <FiltersWrapper>
        <InputFilter
          value={unitFilter}
          onChange={setUnitFilter}
          placeholder="unit"
        />
        <InputFilter
          value={licensePlate}
          onChange={setLicensePlate}
          placeholder="licensePlate"
        />
        <SelectFilter
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { optionLabel: "Active", optionValue: "true" },
            { optionLabel: "Inactive", optionValue: "false" },
          ]}
        />
        <Button
          variant="positive"
          labelKey="create"
          onClick={() => setCreateModalOpen(true)}
        />
      </FiltersWrapper>
      <CursorPagination
        hasNext={results.hasNext}
        hasPrevious={results.hasPrevious}
        currentPage={results.currentPage}
        fetchNextPage={results.fetchNextPage}
        fetchPreviousPage={results.fetchPreviousPage}
      />
      <DataTable
        columns={columns}
        data={results.results}
        getRowKey={(row) => row.id}
        isLoading={results.isLoading}
        onRowClick={(row) => navigate(`/trailer-assets/${row.id}`)}
      />
    </PageLayout>
  );
};

export default TrailerAssetsRoute;
